import { apiSlice } from "../../app/apiSlice";
import { clearCredentials } from "../../app/authSlice";

export const loginSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    authenticateUser: builder.mutation({
      query: (initialPost) => ({
        url: "/api/v1/en/student/auth/logIn",
        method: "POST",
        body: initialPost,
      }),
      invalidatesTags: ["Login"],
    }),
    registerUser: builder.mutation({
      query: (initialPost) => ({
        url: "/api/v1/en/student/auth/signup",
        method: "POST",
        body: initialPost,
      }),
      invalidatesTags: ["Login"],
    }),
    verifyOTP: builder.mutation({
      query: (body) => ({
        url: "/api/v1/en/student/auth/verify-email",
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: ["Login"],
    }),
    signupStep2: builder.mutation({
      query: (body) => ({
        url: "/api/v1/en/student/auth/signup-step-2",
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: ["Login"],
    }),
    sendForgetPasswordEmail: builder.mutation({
      query: (email) => ({
        url: "/api/v1/en/student/auth/send-forget-password-email",
        method: "POST",
        body: { email },
      }),
    }),
    resetPassword: builder.mutation({
      query: ({ code, password, email }) => ({
        url: "/api/v1/en/student/auth/reset-password",
        method: "POST",
        body: { code, password, email },
      }),
    }),
    logoutUser: builder.mutation({
      query: () => ({
        url: "/api/v1/en/student/auth/logout",
        method: "GET",
      }),
      invalidatesTags: ["Login"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(clearCredentials());
        } catch (err) {
          // Still clear credentials on error to force re-login
          dispatch(clearCredentials());
        }
      },
    }),
    getCurrentAccount: builder.query({
      query: () => ({
        url: "/api/v1/en/student/currentAccount",
        method: "GET",
      }),
      providesTags: ["Login"],
    }),
    updateProfile: builder.mutation({
      query: (body) => ({
        url: "/api/v1/en/student/auth/update",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Login"],
    }),
    updatePassword: builder.mutation({
      query: (body) => ({
        url: "/api/v1/en/student/auth/update-password",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Login"],
    }),

    deleteImage: builder.mutation({
      query: (body) => ({
        url: "/api/v1/en/s3-upload/delete-s3-object",
        method: "DELETE",
        body,
      }),
    }),
    uploadProfileImage: builder.mutation({
      query: (formData) => ({
        url: "/api/v1/en/s3-upload/upload-profile-image",
        method: "POST",
        body: formData,
      }),
    }),
    getCountryList: builder.query({
      query: ({ limit = 10 } = {}) => ({
        url: `/api/v1/en/country/list-all?limit=${limit}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        // Extract countries array from nested structure
        return response?.data?.data?.student?.countries || [];
      },
    }),
    getTimezoneList: builder.query({
      query: ({ limit = 10 } = {}) => ({
        url: `/api/v1/en/timezone/list-all?limit=${limit}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        // Log the raw response to see the actual structure
        console.log("🔍 RAW Timezone API Response:", response);
        // Try multiple possible paths
        const timezones =
          response?.data?.data?.student?.timezones ||
          response?.data?.data?.timezones ||
          response?.data?.timezones ||
          response?.timezones ||
          [];
        console.log("🔍 Extracted Timezones:", timezones);
        return timezones;
      },
    }),
    getCourses: builder.query({
      query: () => ({
        url: "/api/v1/en/public/courses?courseType=ielts",
        method: "GET",
      }),
      transformResponse: (response) => {
        return response?.data?.data?.courses || [];
      },
    }),
    getPackages: builder.query({
      query: ({ courseId } = {}) => ({
        url: courseId
          ? `/api/v1/en/public/packages?courseId=${courseId}`
          : "/api/v1/en/public/packages",
        method: "GET",
      }),
      transformResponse: (response) => {
        return response?.data?.data?.packages || [];
      },
    }),
    initiatePayment: builder.mutation({
      query: (body) => ({
        url: "/api/v1/en/student/payment/signup/initiate",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Login"],
    }),
    switchCourse: builder.mutation({
      query: (body) => ({
        url: "/api/v1/en/student/course/switch-course",
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: ["Login", "StudentPackages", "MockTests", "MiniMockTests", "ClassTests"],
    }),
  }),
});

export const {
  useAuthenticateUserMutation,
  useRegisterUserMutation,
  useVerifyOTPMutation,
  useSignupStep2Mutation,
  useSendForgetPasswordEmailMutation,
  useResetPasswordMutation,
  useLogoutUserMutation,
  useDeleteImageMutation,
  useUploadProfileImageMutation,
  useGetCurrentAccountQuery,
  useUpdateProfileMutation,
  useUpdatePasswordMutation,
  useGetCountryListQuery,
  useGetTimezoneListQuery,
  useGetCoursesQuery,
  useGetPackagesQuery,
  useInitiatePaymentMutation,
  useSwitchCourseMutation,
} = loginSlice;
